import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  DragIndicator,
  Save,
  Close,
  CheckCircle,
  WatchLater,
} from "@mui/icons-material";
import {
  getTenantMappings,
  saveTenantMappings,
} from "./DataServices/ReminderService";
import {
  fetchVenmoCandidates,
  fetchVenmoPaymentSummary,
  GetMemberAndGuarntor,
  saveVenmoMemberMappings,
  fetchGroupOccupantsForArh,
} from "./DataServices/ProspectiveTenantDataService";
import {
  IPublicTenantLead,
  IAutomaticResponseHistory,
  AutoResponseDetailsinfo,
  MappingItem,
  MemberGuarantorInfo,
  IVenmoCandidate,
  IConfirmedAppfolioAssignment,
} from "./Models";

import Loading from "./Loading";
import MemberStatusFlow from "./MemberStatusFlow";

interface AppfolioUnsignedListProps {
  arhResponses: IAutomaticResponseHistory[];
  appfolioData: IPublicTenantLead[];
  memberGuarantorInfos: { [key: string]: AutoResponseDetailsinfo };
  mappingInfo: {
    [key: string]: { totalMembers: number; mappedMembers: number };
  };
  memoizedCalculations: {
    [key: string]: {
      showingStatus: string;
      externalFormStatus: { status: string; color: string };
      appfolioStatus: { status: string; color: string };
      venmoStatus: { status: string; color: string };
      groupSize: number;
    };
  };
  onMappingsUpdated?: () => Promise<void> | void;
  // When set, pre-selects this group (arhId) on mount/update.
  initialSelectedArhId?: string;
  confirmedAppfolioAssignments?: IConfirmedAppfolioAssignment[];
}

interface MappedAppfolioMember extends IPublicTenantLead {
  role: "tenant" | "guarantor";
  displayName: string;
  dragId: string;
  // "publicLead" = legacy flat PublicTenantLead (Source 1, default).
  // "groupOccupant" = group-aware AppFolio scrape (Source 2) — same shape via field
  // mapping (apartmentTitle -> apartmentName, isCosigner -> isGuarantor, etc.) so the
  // rest of this component's render/filter logic doesn't need to special-case it.
  source: "publicLead" | "groupOccupant";
  // Live-computed suggestion from the backend (not a saved mapping) — mirrors
  // IVenmoCandidate.matchedMemberId. Only ever set on "groupOccupant" entries.
  matchedMemberId?: string | null;
}

// A name extracted from Venmo emails, draggable in "Venmo" mapping mode
interface MappedVenmoCandidate {
  dragId: string;
  displayName: string;
  key: string;
  amount: number;
  paymentDate?: string | null;
  transactionId?: string | null;
  isExplicitMatch?: boolean;
}

// Represents a slot for a form member with an optional matched public lead or Venmo candidate
interface FormMemberSlot {
  formMember: MemberGuarantorInfo;
  formMemberIndex: number;
  matchedLead: MappedAppfolioMember | MappedVenmoCandidate | null;
}

const AppfolioUnsignedList: React.FC<AppfolioUnsignedListProps> = ({
  arhResponses,
  appfolioData,
  memberGuarantorInfos,
  mappingInfo,
  memoizedCalculations,
  onMappingsUpdated,
  initialSelectedArhId,
  confirmedAppfolioAssignments,
}) => {
  const [selectedArhId, setSelectedArhId] = useState<string>("");

  // Pre-select the group passed in from the Group Details page (e.g. when
  // a row is clicked there to jump straight into matching for that group).
  useEffect(() => {
    if (initialSelectedArhId) {
      setSelectedArhId(initialSelectedArhId);
    }
  }, [initialSelectedArhId]);
  const [existingMappings, setExistingMappings] = useState<{
    [key: string]: MappingItem[];
  }>({});
  const [loading, setLoading] = useState(false);
  const [arhSearchQuery, setArhSearchQuery] = useState("");
  const [appfolioSearchQuery, setAppfolioSearchQuery] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"success" | "error" | null>(
    null,
  );
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(
    null,
  );
  // formMemberSlots: keyed by formMemberIndex -> matched lead
  const [slotMappings, setSlotMappings] = useState<{
    [formMemberIndex: string]: MappedAppfolioMember | null;
  }>({});
  const [draggedLead, setDraggedLead] = useState<MappedAppfolioMember | null>(
    null,
  );
  const [dragOverSlot, setDragOverSlot] = useState<string | null>(null);

  // "appfolio" = map against AppFolio public tenant leads (default, existing behavior)
  // "venmo" = map against names extracted from this listing's Venmo emails
  const [mappingMode, setMappingMode] = useState<"appfolio" | "venmo">(
    "appfolio",
  );
  const [venmoCandidatesByArh, setVenmoCandidatesByArh] = useState<{
    [arhId: string]: IVenmoCandidate[];
  }>({});
  // Source-2 (group-aware AppFolio scrape) candidates, cached per ARH — mirrors
  // venmoCandidatesByArh's caching strategy.
  const [groupOccupantsByArh, setGroupOccupantsByArh] = useState<{
    [arhId: string]: MappedAppfolioMember[];
  }>({});
  const [venmoSlotMappings, setVenmoSlotMappings] = useState<{
    [formMemberIndex: string]: MappedVenmoCandidate | null;
  }>({});
  const [draggedVenmoCandidate, setDraggedVenmoCandidate] =
    useState<MappedVenmoCandidate | null>(null);
  const [suggestedSlotDragIds, setSuggestedSlotDragIds] = useState<Set<string>>(new Set());
  const [suggestedVenmoSlotKeys, setSuggestedVenmoSlotKeys] = useState<Set<string>>(new Set());

  // Venmo activity for the selected group (mirrors the purple hint box in Matcher View)
  const [venmoPayments, setVenmoPayments] = useState<
    {
      payerName?: string;
      amount?: number;
      paymentDate?: string;
      transactionId?: string;
    }[]
  >([]);
  const [venmoConfirmedMembers, setVenmoConfirmedMembers] = useState<
    MappingItem[]
  >([]);

  useEffect(() => {
    if (!selectedArhId) {
      setVenmoPayments([]);
      setVenmoConfirmedMembers([]);
      return;
    }
    let cancelled = false;
    fetchVenmoPaymentSummary(selectedArhId).then((res) => {
      if (!cancelled) setVenmoPayments(res?.payments || []);
    });
    GetMemberAndGuarntor(selectedArhId).then((data) => {
      if (!cancelled) setVenmoConfirmedMembers(Array.isArray(data) ? data : []);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedArhId]);

  useEffect(() => {
    const fetchMappings = async () => {
      if (arhResponses.length === 0) return;

      setLoading(true);
      try {
        const mappingsMap: { [key: string]: MappingItem[] } = {};
        await Promise.all(
          arhResponses.map(async (arh) => {
            try {
              const mappings = await getTenantMappings(arh.id);
              mappingsMap[arh.id] = mappings || [];
            } catch (error) {
              mappingsMap[arh.id] = [];
            }
          }),
        );
        setExistingMappings(mappingsMap);
      } catch (error) {
        console.error("Error fetching mappings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMappings();
  }, [arhResponses]);

  // Fetch Source-2 (group-aware AppFolio scrape) candidates for the selected group —
  // mirrors the Venmo candidate fetch effect below exactly (same arhId-keyed caching).
  useEffect(() => {
    if (!selectedArhId || groupOccupantsByArh[selectedArhId]) return;
    let cancelled = false;
    (async () => {
      try {
        const occupants = await fetchGroupOccupantsForArh(selectedArhId);
        if (cancelled) return;
        const mapped: MappedAppfolioMember[] = (occupants || [])
          .filter((o) => o.name)
          .map((o) => ({
            id: o.id,
            name: o.name,
            email: o.email,
            apartmentName: o.apartmentTitle,
            phoneNo: o.phone || "",
            createdAtUtc: o.createdAtUtc,
            isGuarantor: o.isCosigner,
            role: o.isCosigner ? "guarantor" : "tenant",
            displayName: o.name,
            dragId: o.id,
            source: "groupOccupant",
            matchedMemberId: o.matchedMemberId,
          }));
        setGroupOccupantsByArh((prev) => ({
          ...prev,
          [selectedArhId]: mapped,
        }));
      } catch (error) {
        if (!cancelled) {
          setGroupOccupantsByArh((prev) => ({
            ...prev,
            [selectedArhId]: [],
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedArhId, groupOccupantsByArh]);

  // All public tenant leads (Source 1) + group-occupant candidates for the selected
  // group (Source 2) as one unified draggable list, tagged by source.
  // Same real applicant can be scraped by both AppFolio sources independently.
  // Identity key prefers email (more reliable); falls back to name when email is blank.
  const appfolioIdentityKey = (name?: string, email?: string) => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    if (normalizedEmail) return `email:${normalizedEmail}`;
    return `name:${(name || "").trim().toLowerCase()}`;
  };

  // Member ids belonging to the SELECTED group's own roster — a confirmed
  // assignment pointing to one of these is normal (this group's own mapping,
  // shown via existingMappings elsewhere), not a cross-group conflict.
  const ownMemberIds = useMemo(
    () =>
      new Set(
        (
          memberGuarantorInfos[selectedArhId]?.memberAndGaurantorInfo || []
        ).map((m) => m.id),
      ),
    [memberGuarantorInfos, selectedArhId],
  );

  // Leads/occupants already confirmed to a DIFFERENT group's member — excluded
  // below so the same real applicant can't be dragged onto a second, unrelated
  // member here and saved twice (SaveMappings would also reject it, but it's
  // clearer to never offer it as a candidate in the first place). Tracked by
  // BOTH raw id and identity (name+email): the same real person is often scraped
  // independently into Source 1 and Source 2 as two different rows/ids, so id
  // alone would only hide whichever specific row got confirmed, leaving the
  // other source's copy of the same person looking available.
  const confirmedElsewhereIds = useMemo(() => {
    const ids = new Set<string>();
    (confirmedAppfolioAssignments || []).forEach((a) => {
      if (ownMemberIds.has(a.memberId)) return;
      if (a.appfolioId) ids.add(a.appfolioId);
      if (a.groupOccupantId) ids.add(a.groupOccupantId);
    });
    return ids;
  }, [confirmedAppfolioAssignments, ownMemberIds]);

  const currentListingId = useMemo(
    () => arhResponses.find((a) => a.id === selectedArhId)?.listingID ?? "",
    [arhResponses, selectedArhId],
  );

  const confirmedElsewhereIdentityKeys = useMemo(() => {
    const keys = new Set<string>();
    (confirmedAppfolioAssignments || []).forEach((a) => {
      if (ownMemberIds.has(a.memberId)) return;
      // Only apply the identity-key (email) filter for assignments from the SAME listing.
      // Cross-listing: the same person legitimately has separate applications for different
      // apartments — block by raw ID only, so their other-apartment leads remain visible.
      if (a.listingId === currentListingId) {
        keys.add(appfolioIdentityKey(a.name, a.email));
      }
    });
    return keys;
  }, [confirmedAppfolioAssignments, ownMemberIds, currentListingId]);

  const processedAppfolioData = useMemo(() => {
    const processed: MappedAppfolioMember[] = [];
    const seenIdentities = new Set<string>();

    appfolioData.forEach((member) => {
      if (
        member.name &&
        !confirmedElsewhereIds.has(member.id) &&
        !confirmedElsewhereIdentityKeys.has(
          appfolioIdentityKey(member.name, member.email),
        )
      ) {
        processed.push({
          ...member,
          role: member.isGuarantor ? "guarantor" : "tenant",
          displayName: member.name,
          dragId: member.id,
          source: "publicLead",
        });
        seenIdentities.add(appfolioIdentityKey(member.name, member.email));
      }
    });

    if (selectedArhId && groupOccupantsByArh[selectedArhId]) {
      // Source 1 already wins everywhere else (auto-mapping), so a Source-2 entry that's
      // really the same person as an already-listed Source-1 lead is skipped here too —
      // otherwise the same applicant shows up twice with no way to tell they're one person.
      groupOccupantsByArh[selectedArhId].forEach((occupant) => {
        if (confirmedElsewhereIds.has(occupant.id)) return;
        const key = appfolioIdentityKey(occupant.displayName, occupant.email);
        if (confirmedElsewhereIdentityKeys.has(key)) return;
        if (seenIdentities.has(key)) return;
        seenIdentities.add(key);
        processed.push(occupant);
      });
    }

    return processed;
  }, [
    appfolioData,
    selectedArhId,
    groupOccupantsByArh,
    confirmedElsewhereIds,
    confirmedElsewhereIdentityKeys,
  ]);

  const filteredArhResponses = useMemo(() => {
    return arhResponses.filter(
      (arh) =>
        `${arh.firstName} ${arh.lastName}`
          .toLowerCase()
          .includes(arhSearchQuery.toLowerCase()) ||
        arh.title.toLowerCase().includes(arhSearchQuery.toLowerCase()) ||
        arh.email.toLowerCase().includes(arhSearchQuery.toLowerCase()),
    );
  }, [arhResponses, arhSearchQuery]);

  const filteredAppfolioData = useMemo(() => {
    const query = appfolioSearchQuery.toLowerCase().trim();
    return processedAppfolioData.filter((member) => {
      const nameMatch = member.displayName.toLowerCase().includes(query);
      const emailMatch = (member.email || "").toLowerCase().includes(query);
      const aptMatch = (member.apartmentName || "")
        .toLowerCase()
        .includes(query);
      return nameMatch || emailMatch || aptMatch;
    });
  }, [processedAppfolioData, appfolioSearchQuery]);

  // The IDs of public leads already assigned to slots (excluding suggestion-only pre-fills)
  const assignedLeadIds = useMemo(() => {
    const ids = new Set<string>();
    Object.values(slotMappings).forEach((lead) => {
      if (lead && !suggestedSlotDragIds.has(lead.dragId)) ids.add(lead.dragId);
    });
    return ids;
  }, [slotMappings, suggestedSlotDragIds]);

  // Available = not yet assigned to any slot
  const availableLeads = useMemo(() => {
    return filteredAppfolioData.filter(
      (member) => !assignedLeadIds.has(member.dragId),
    );
  }, [filteredAppfolioData, assignedLeadIds]);

  // Fetch Venmo-extracted names for the selected group's listing (cached per ARH)
  useEffect(() => {
    if (!selectedArhId || venmoCandidatesByArh[selectedArhId]) return;
    let cancelled = false;
    (async () => {
      try {
        const candidates = await fetchVenmoCandidates(selectedArhId);
        if (!cancelled) {
          setVenmoCandidatesByArh((prev) => ({
            ...prev,
            [selectedArhId]: candidates || [],
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setVenmoCandidatesByArh((prev) => ({
            ...prev,
            [selectedArhId]: [],
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedArhId, venmoCandidatesByArh]);

  // All Venmo names extracted for the selected group, as draggable items
  const processedVenmoCandidates = useMemo(() => {
    const candidates = venmoCandidatesByArh[selectedArhId] || [];
    return candidates.map((c) => ({
      dragId: c.key,
      displayName: c.name,
      key: c.key,
      amount: c.amount,
      paymentDate: c.paymentDate,
      transactionId: c.transactionId,
      isExplicitMatch: c.isExplicitMatch ?? false,
    }));
  }, [venmoCandidatesByArh, selectedArhId]);

  const filteredVenmoCandidates = useMemo(() => {
    const query = appfolioSearchQuery.toLowerCase().trim();
    return processedVenmoCandidates.filter((c) =>
      c.displayName.toLowerCase().includes(query),
    );
  }, [processedVenmoCandidates, appfolioSearchQuery]);

  // The keys of Venmo candidates already assigned to slots (excluding suggestion-only pre-fills)
  const assignedVenmoKeys = useMemo(() => {
    const keys = new Set<string>();
    Object.values(venmoSlotMappings).forEach((c) => {
      if (c && !suggestedVenmoSlotKeys.has(c.dragId)) keys.add(c.dragId);
    });
    return keys;
  }, [venmoSlotMappings, suggestedVenmoSlotKeys]);

  const availableVenmoCandidates = useMemo(() => {
    return filteredVenmoCandidates.filter(
      (c) => !assignedVenmoKeys.has(c.dragId),
    );
  }, [filteredVenmoCandidates, assignedVenmoKeys]);

  // Build form member slots from selected ARH info + auto-match or existing mappings
  useEffect(() => {
    if (!selectedArhId || !memberGuarantorInfos[selectedArhId]) {
      setSlotMappings({});
      return;
    }

    const info = memberGuarantorInfos[selectedArhId];
    const selectedArh = arhResponses.find((arh) => arh.id === selectedArhId);
    if (!selectedArh || !info.memberAndGaurantorInfo) {
      setSlotMappings({});
      return;
    }

    const mappings = existingMappings[selectedArhId] || [];
    const newSlots: { [key: string]: MappedAppfolioMember | null } = {};
    const newSuggestedIds = new Set<string>();

    info.memberAndGaurantorInfo.forEach((formMember, index) => {
      // Find existing DB mapping via getTenantMappings (from TenantDataToAppfolioDataMap)
      const existingMapping = mappings.find((m) => m.id === formMember.id);
      if (existingMapping?.mappedTo) {
        const lead = processedAppfolioData.find(
          (l) => l.id === existingMapping.mappedTo!.id,
        );
        if (lead) {
          newSlots[`${index}`] = lead;
        } else {
          // If not found in processedAppfolioData, construct a fallback MappedAppfolioMember so it displays correctly
          const fallbackLead: MappedAppfolioMember = {
            ...existingMapping.mappedTo,
            role: existingMapping.mappedTo.isGuarantor ? "guarantor" : "tenant",
            displayName: existingMapping.mappedTo.name,
            dragId: existingMapping.mappedTo.id,
            source: "publicLead",
          };
          newSlots[`${index}`] = fallbackLead;
        }
      } else if (existingMapping?.mappedToGroupOccupant) {
        const occupant = existingMapping.mappedToGroupOccupant;
        const lead = processedAppfolioData.find(
          (l) => l.id === occupant.id && l.source === "groupOccupant",
        );
        if (lead) {
          newSlots[`${index}`] = lead;
        } else {
          // Group occupant not (yet) in this listing's cached candidates — construct
          // a fallback so the saved mapping still displays correctly.
          const fallbackOccupant: MappedAppfolioMember = {
            id: occupant.id,
            name: occupant.name,
            email: occupant.email,
            apartmentName: occupant.apartmentTitle,
            phoneNo: occupant.phone || "",
            createdAtUtc: occupant.createdAtUtc,
            isGuarantor: occupant.isCosigner,
            role: occupant.isCosigner ? "guarantor" : "tenant",
            displayName: occupant.name,
            dragId: occupant.id,
            source: "groupOccupant",
          };
          newSlots[`${index}`] = fallbackOccupant;
        }
      } else {
        // No saved mapping yet — fall back to the backend's live-computed suggestion
        // (matchedMemberId), same as how Venmo candidates pre-fill an unconfirmed slot.
        // Nothing has been written to the DB for this; it only becomes real on Save.
        const suggested = processedAppfolioData.find(
          (l) => l.source === "groupOccupant" && l.matchedMemberId === formMember.id,
        );
        newSlots[`${index}`] = suggested || null;
        if (suggested) newSuggestedIds.add(suggested.dragId);
      }
    });

    setSlotMappings(newSlots);
    setSuggestedSlotDragIds(newSuggestedIds);
  }, [
    selectedArhId,
    memberGuarantorInfos,
    existingMappings,
    arhResponses,
    processedAppfolioData,
  ]);

  // Build Venmo slot mappings, pre-seeding each form member with the Venmo
  // candidate the backend's automatic fuzzy match (>= 0.80) already linked
  // (via matchedMemberId), so admins only need to fix the misses.
  useEffect(() => {
    if (!selectedArhId || !memberGuarantorInfos[selectedArhId]) {
      setVenmoSlotMappings({});
      return;
    }

    const info = memberGuarantorInfos[selectedArhId];
    if (!info.memberAndGaurantorInfo) {
      setVenmoSlotMappings({});
      return;
    }

    const candidates = venmoCandidatesByArh[selectedArhId] || [];
    const newSlots: { [key: string]: MappedVenmoCandidate | null } = {};
    const newSuggestedKeys = new Set<string>();

    info.memberAndGaurantorInfo.forEach((formMember, index) => {
      const match = candidates.find((c) => c.matchedMemberId === formMember.id);
      if (match) {
        newSlots[`${index}`] = {
          dragId: match.key,
          displayName: match.name,
          key: match.key,
          amount: match.amount,
          paymentDate: match.paymentDate,
          transactionId: match.transactionId,
          isExplicitMatch: match.isExplicitMatch ?? false,
        };
        if (!match.isExplicitMatch) newSuggestedKeys.add(match.key);
      } else {
        newSlots[`${index}`] = null;
      }
    });

    setVenmoSlotMappings(newSlots);
    setSuggestedVenmoSlotKeys(newSuggestedKeys);
  }, [selectedArhId, memberGuarantorInfos, venmoCandidatesByArh]);

  // Drag handlers for leads from "All Names"
  const handleDragStart = (e: React.DragEvent, lead: MappedAppfolioMember) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedLead(null);
    setDragOverSlot(null);
  };

  // Drag handlers for Venmo candidates from "Available Leads" (Venmo mode)
  const handleVenmoDragStart = (
    e: React.DragEvent,
    candidate: MappedVenmoCandidate,
  ) => {
    setDraggedVenmoCandidate(candidate);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleVenmoDragEnd = () => {
    setDraggedVenmoCandidate(null);
    setDragOverSlot(null);
  };

  const handleSlotDragOver = (e: React.DragEvent, slotKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverSlot(slotKey);
  };

  const handleSlotDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDropOnSlot = (e: React.DragEvent, slotKey: string) => {
    e.preventDefault();
    setDragOverSlot(null);

    if (mappingMode === "venmo") {
      if (!draggedVenmoCandidate) return;
      setVenmoSlotMappings((prev) => {
        const updated = { ...prev };
        // If this candidate is already assigned elsewhere, remove it from that slot
        Object.keys(updated).forEach((k) => {
          if (updated[k]?.dragId === draggedVenmoCandidate.dragId) {
            updated[k] = null;
          }
        });
        updated[slotKey] = draggedVenmoCandidate;
        return updated;
      });
      setSuggestedVenmoSlotKeys((prev) => {
        const next = new Set(prev);
        next.delete(draggedVenmoCandidate.dragId);
        return next;
      });
      setDraggedVenmoCandidate(null);
      return;
    }

    if (!draggedLead) return;

    setSlotMappings((prev) => {
      const updated = { ...prev };
      // If this lead is already assigned elsewhere, remove it from that slot
      Object.keys(updated).forEach((k) => {
        if (updated[k]?.dragId === draggedLead.dragId) {
          updated[k] = null;
        }
      });
      updated[slotKey] = draggedLead;
      return updated;
    });
    setSuggestedSlotDragIds((prev) => {
      const next = new Set(prev);
      next.delete(draggedLead.dragId);
      return next;
    });
    setDraggedLead(null);
  };

  const handleRemoveSlotMapping = (slotKey: string) => {
    if (mappingMode === "venmo") {
      const removedVenmo = venmoSlotMappings[slotKey];
      setVenmoSlotMappings((prev) => ({ ...prev, [slotKey]: null }));
      if (removedVenmo) {
        setSuggestedVenmoSlotKeys((prev) => { const next = new Set(prev); next.delete(removedVenmo.dragId); return next; });
      }
      return;
    }
    const removedLead = slotMappings[slotKey];
    setSlotMappings((prev) => ({ ...prev, [slotKey]: null }));
    if (removedLead) {
      setSuggestedSlotDragIds((prev) => { const next = new Set(prev); next.delete(removedLead.dragId); return next; });
    }
  };

  const handleSave = async () => {
    if (!selectedArhId) return;
    setSaveLoading(true);
    setSaveErrorMessage(null);

    try {
      const info = memberGuarantorInfos[selectedArhId];
      const formMembers = info?.memberAndGaurantorInfo || [];

      if (formMembers.length === 0) {
        // No form members, nothing to save
        setSaveStatus("success");
        setTimeout(() => setSaveStatus(null), 3000);
        setSaveLoading(false);
        return;
      }

      if (mappingMode === "venmo") {
        const mappings = formMembers.map((formMember, index) => ({
          memberId: formMember.id,
          venmoKey: venmoSlotMappings[`${index}`]?.key || null,
        }));

        await saveVenmoMemberMappings(selectedArhId, mappings);

        // Invalidate the stale Venmo candidate cache so the fetch effect
        // re-runs and picks up updated matchedMemberId values from the server.
        // Without this, the slot-building effect re-fills cleared slots from
        // the old matchedMemberId and the candidate never reappears as available.
        setVenmoCandidatesByArh((prev) => {
          const { [selectedArhId]: _evicted, ...rest } = prev;
          return rest;
        });

        await onMappingsUpdated?.();

        setSaveStatus("success");
        setSaveLoading(false);
        setTimeout(() => setSaveStatus(null), 3000);
        return;
      }

      const mappingData = formMembers.map((formMember, index) => {
        const matchedLead = slotMappings[`${index}`] || null;
        const isGroupOccupant = matchedLead?.source === "groupOccupant";
        return {
          id: formMember.id,
          arhId: selectedArhId,
          name: formMember.name,
          email: formMember.email || "",
          isGuarantor: formMember.isGuarantor,
          isDeleted: false,
          appfolioId: matchedLead && !isGroupOccupant ? matchedLead.id : null,
          groupOccupantId: matchedLead && isGroupOccupant ? matchedLead.id : null,
          mappedTo:
            matchedLead && !isGroupOccupant
              ? {
                  id: matchedLead.id,
                  name: matchedLead.name,
                  email: matchedLead.email,
                  apartmentName: matchedLead.apartmentName,
                  phoneNo: matchedLead.phoneNo,
                  createdAtUtc: matchedLead.createdAtUtc,
                  isGuarantor: matchedLead.isGuarantor,
                }
              : null,
          mappedToGroupOccupant:
            matchedLead && isGroupOccupant
              ? {
                  id: matchedLead.id,
                  name: matchedLead.name,
                  email: matchedLead.email,
                  phone: matchedLead.phoneNo,
                  isCosigner: matchedLead.isGuarantor,
                  apartmentTitle: matchedLead.apartmentName,
                  createdAtUtc: matchedLead.createdAtUtc,
                }
              : null,
          isConfirmed: !!matchedLead,
        };
      });

      await saveTenantMappings(mappingData);

      const updatedMappings = await getTenantMappings(selectedArhId);
      setExistingMappings((prev) => ({
        ...prev,
        [selectedArhId]: updatedMappings || [],
      }));

      // Invalidate stale groupOccupant cache so the slot-building effect
      // re-fetches with updated matchedMemberId values. Without this, clearing
      // a groupOccupant mapping still re-fills the slot from the stale cache
      // (the else branch pre-seeds from matchedMemberId) and the lead never
      // reappears as available.
      setGroupOccupantsByArh((prev) => {
        const { [selectedArhId]: _evicted, ...rest } = prev;
        return rest;
      });

      await onMappingsUpdated?.();

      setSaveStatus("success");
    } catch (error) {
      console.error("Error saving mappings:", error);
      setSaveStatus("error");
      setSaveErrorMessage(
        error instanceof Error ? error.message : "Error saving mappings",
      );
    } finally {
      setSaveLoading(false);
      setTimeout(() => {
        setSaveStatus(null);
        setSaveErrorMessage(null);
      }, 5000);
    }
  };

  const selectedArhResponse = arhResponses.find(
    (arh) => arh.id === selectedArhId,
  );
  const selectedMemberInfo = selectedArhId
    ? memberGuarantorInfos[selectedArhId]
    : null;

  const formMemberSlots: FormMemberSlot[] = useMemo(() => {
    if (!selectedMemberInfo?.memberAndGaurantorInfo) return [];
    const activeSlotMappings =
      mappingMode === "venmo" ? venmoSlotMappings : slotMappings;
    return selectedMemberInfo.memberAndGaurantorInfo.map(
      (formMember, index) => ({
        formMember,
        formMemberIndex: index,
        matchedLead: activeSlotMappings[`${index}`] || null,
      }),
    );
  }, [selectedMemberInfo, slotMappings, venmoSlotMappings, mappingMode]);

  const matchedCount = formMemberSlots.filter((s) => s.matchedLead).length;

  const selectedCalculations = selectedArhId
    ? memoizedCalculations[selectedArhId]
    : undefined;
  const selectedMappingInfo = selectedArhId
    ? mappingInfo[selectedArhId]
    : undefined;
  const isSelectedMandatory =
    !!selectedArhResponse &&
    (selectedArhResponse.workflowType === 0 || selectedArhResponse.isMandatory);
  const venmoConfirmedCount = venmoConfirmedMembers.filter(
    (m) => m.isManuallyConfirmed,
  ).length;

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-4 bg-slate-50/50 p-3 lg:p-4 w-full">
      {/* Left Section - List & Group Details */}
      <div className="w-full lg:w-1/2 flex flex-col overflow-hidden bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 min-h-0">
        {/* Top Section - ARH Responses */}
        <div className="h-[300px] md:h-[360px] flex flex-col border-b border-slate-100 bg-slate-50/30">
          <div className="px-4 py-2 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 bg-white">
            <h2 className="text-base md:text-lg font-semibold text-slate-800 tracking-tight">
              ARH Responses
            </h2>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search groups..."
                value={arhSearchQuery}
                onChange={(e) => setArhSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Table Header + Body share a horizontally scrollable container so new
              columns (e.g. Venmo Status) don't get squeezed on smaller screens */}
          <div className="flex-1 overflow-auto">
            <div className="min-w-[900px] md:min-w-[1000px]">
              {/* Table Header */}
              <div className="grid grid-cols-[repeat(15,minmax(0,1fr))] gap-2 md:gap-3 p-2 md:p-3 bg-gray-50 border-b border-gray-200 text-xs md:text-sm font-medium text-gray-700 sticky top-0 z-10">
                <div className="col-span-1"></div>
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">External Form</div>
                <div className="col-span-3">Public Lead Status</div>
                <div className="col-span-3">Venmo Status</div>
              </div>

              {/* Table Body */}
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loading />
                </div>
              ) : filteredArhResponses.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No ARH responses available
                </div>
              ) : (
                filteredArhResponses.map((arh) => {
                  const calculations = memoizedCalculations[arh.id];
                  if (!calculations) return null;

                  return (
                    <div
                      key={arh.id}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        selectedArhId === arh.id
                          ? "bg-blue-50 border-blue-200"
                          : ""
                      }`}
                      onClick={() => setSelectedArhId(arh.id)}
                    >
                      {/* Row Layout */}
                      <div className="grid grid-cols-[repeat(15,minmax(0,1fr))] gap-2 md:gap-3 p-2 md:p-3">
                        <div className="col-span-1 flex items-center">
                          <input
                            type="radio"
                            name="arhSelection"
                            checked={selectedArhId === arh.id}
                            onChange={() => setSelectedArhId(arh.id)}
                            className="mr-1"
                          />
                        </div>

                        {/* Name */}
                        <div className="col-span-3 flex items-center">
                          <div className="truncate">
                            <div className="font-medium text-sm text-gray-900 truncate">
                              {arh.firstName} {arh.lastName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {arh.title}
                            </div>
                          </div>
                        </div>

                        {/* Email */}
                        <div className="col-span-3 flex items-center">
                          <div className="text-sm text-gray-600 truncate">
                            {arh.email}
                          </div>
                        </div>

                        {/* External Form Status */}
                        <div className="col-span-2 flex items-center">
                          <span
                            className={`px-2 py-1 rounded text-xs truncate ${calculations.externalFormStatus.color}`}
                          >
                            {calculations.externalFormStatus.status}
                          </span>
                        </div>

                        {/* Public Lead Status */}
                        <div className="col-span-3 flex items-center">
                          <span
                            className={`px-2 py-1 rounded text-xs ${calculations.appfolioStatus.color}`}
                          >
                            {calculations.appfolioStatus.status}
                          </span>
                        </div>

                        {/* Venmo Status */}
                        <div className="col-span-3 flex items-center">
                          <span
                            className={`px-2 py-1 rounded text-xs ${calculations.venmoStatus.color}`}
                          >
                            {calculations.venmoStatus.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - Group Details */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm px-4 py-2 border-b border-slate-100 z-10">
            <h2 className="text-lg font-semibold text-slate-800 tracking-tight">
              Group Details
            </h2>
          </div>

          {selectedArhResponse && selectedMemberInfo ? (
            <div className="p-3 space-y-3">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedArhResponse.firstName} {selectedArhResponse.lastName}
                  's Group
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedArhResponse.title} •{" "}
                  {selectedMemberInfo.numOfBedrooms || 0} bedrooms
                </p>
              </div>

              {/* Group Status Flow */}
              {selectedCalculations && (
                <MemberStatusFlow
                  arhid={selectedArhId}
                  info={selectedMemberInfo}
                  mappingInfo={selectedMappingInfo}
                  appointmentStatus={selectedArhResponse.appointmentStatus}
                  isMandatory={isSelectedMandatory}
                  getExternalFormStatus={() =>
                    selectedCalculations.externalFormStatus
                  }
                  getAppfolioStatus={() => selectedCalculations.appfolioStatus}
                />
              )}

              {/* Venmo activity hint - mirrors Matcher View */}
              {(venmoPayments.length > 0 || venmoConfirmedCount > 0) && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-purple-900">
                    Venmo activity for this listing — independent of AppFolio
                    mapping
                  </div>
                  {venmoConfirmedCount > 0 && (
                    <div className="text-xs text-purple-800 mt-1">
                      {venmoConfirmedCount} member
                      {venmoConfirmedCount > 1 ? "s" : ""} confirmed via Venmo
                      name match (
                      {venmoConfirmedMembers
                        .filter((m) => m.isManuallyConfirmed)
                        .map((m) => m.name)
                        .join(", ")}
                      )
                    </div>
                  )}
                  {venmoPayments.length > 0 ? (
                    <ul className="text-xs text-purple-800 mt-1 space-y-0.5">
                      {venmoPayments.map((p, idx) => (
                        <li key={p.transactionId || idx}>
                          ${Number(p.amount ?? 0).toFixed(2)}
                          {p.payerName ? ` from ${p.payerName}` : ""}
                          {p.paymentDate
                            ? ` on ${new Date(p.paymentDate).toLocaleDateString()}`
                            : ""}{" "}
                          — counts toward the "paid" requirement for the
                          Mandatory Workflow gate.
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs text-purple-700 mt-1">
                      No processed Venmo payments found for this listing yet.
                    </div>
                  )}
                </div>
              )}

              <div>
                <h4 className="font-medium text-gray-700 mb-2">
                  Form Submissions
                </h4>
                {selectedMemberInfo.memberAndGaurantorInfo &&
                selectedMemberInfo.memberAndGaurantorInfo.length > 0 ? (
                  <div className="space-y-2">
                    {selectedMemberInfo.memberAndGaurantorInfo.map(
                      (member, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 p-3 rounded flex justify-between items-center"
                        >
                          <div>
                            <div className="font-medium">{member.name}</div>
                            <div className="text-xs text-gray-500">
                              {member.isGuarantor ? "Guarantor" : "Tenant"}
                            </div>
                          </div>
                          {member.appfolioId && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 text-xs rounded">
                              Mapped
                            </span>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                    No form submissions yet
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              <p>Select an ARH response to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Mapping Panel */}
      <div className="w-full lg:w-1/2 flex flex-col overflow-hidden bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 min-h-0">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 flex-shrink-0 gap-3">
          <h2 className="text-lg font-semibold text-slate-800 tracking-tight">
            Mapping
          </h2>
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-200/70 rounded-lg p-0.5">
              <button
                onClick={() => setMappingMode("appfolio")}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  mappingMode === "appfolio"
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                AppFolio
              </button>
              <button
                onClick={() => setMappingMode("venmo")}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  mappingMode === "venmo"
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Venmo
              </button>
            </div>
            {selectedArhId && formMemberSlots.length > 0 && (
              <button
                onClick={handleSave}
                disabled={saveLoading}
                className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-3.5 h-3.5" />
                {saveLoading ? "Saving..." : "Save"}
              </button>
            )}
          </div>
        </div>

        {!selectedArhId ? (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
            Select a group from the list to map
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Form Members (drop targets) */}
            <div className="w-1/2 border-r border-slate-100 flex flex-col overflow-hidden bg-white">
              <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-100">
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Form Members
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {formMemberSlots.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-center">
                    <p className="text-sm text-slate-400">
                      No form members to map
                    </p>
                  </div>
                ) : (
                  formMemberSlots.map((slot) => {
                    const slotKey = `${slot.formMemberIndex}`;
                    const isOver = dragOverSlot === slotKey;
                    const isVenmoSuggestedSlot =
                      mappingMode === "venmo" &&
                      !!slot.matchedLead &&
                      !(slot.matchedLead as MappedVenmoCandidate).isExplicitMatch;
                    // AppFolio equivalent: the slot is filled by the live matchedMemberId
                    // suggestion (see the slot-building effect above), not by a saved
                    // TenantDataToAppfolioDataMap row — nothing's been written to the DB
                    // for it yet, same "pending until Save" state as a Venmo suggestion.
                    const isAppfolioSuggestedSlot =
                      mappingMode === "appfolio" &&
                      !!slot.matchedLead &&
                      (slot.matchedLead as MappedAppfolioMember).source ===
                        "groupOccupant" &&
                      !(existingMappings[selectedArhId] || []).some(
                        (m) =>
                          m.id === slot.formMember.id &&
                          m.mappedToGroupOccupant?.id ===
                            (slot.matchedLead as MappedAppfolioMember).id,
                      );
                    const isSuggestedSlot =
                      isVenmoSuggestedSlot || isAppfolioSuggestedSlot;
                    return (
                      <div
                        key={slotKey}
                        onDragOver={(e) => handleSlotDragOver(e, slotKey)}
                        onDragLeave={handleSlotDragLeave}
                        onDrop={(e) => handleDropOnSlot(e, slotKey)}
                        className={`relative rounded-xl border-2 p-3 transition-all duration-200 ${
                          isOver
                            ? "border-blue-400 bg-blue-50/50 scale-[1.02] shadow-sm"
                            : isSuggestedSlot
                              ? "border-dashed border-amber-300 bg-amber-50/30"
                              : slot.matchedLead
                                ? "border-emerald-200 bg-emerald-50/30"
                                : "border-dashed border-slate-200 bg-slate-50/50 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {/* Member name + role */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                slot.formMember.isGuarantor
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-indigo-100 text-indigo-700"
                              }`}
                            >
                              {slot.formMember.isGuarantor ? "Guar" : "Ten"}
                            </span>
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-semibold text-slate-700 truncate">
                                {slot.formMember.name}
                              </span>
                              {slot.formMember.email && (
                                <span
                                  className="text-[11px] text-slate-400 truncate -mt-0.5"
                                  title={slot.formMember.email}
                                >
                                  {slot.formMember.email}
                                </span>
                              )}
                            </div>
                          </div>
                          {slot.matchedLead && (
                            <button
                              onClick={() => handleRemoveSlotMapping(slotKey)}
                              className="flex-shrink-0 ml-1 text-slate-300 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors"
                            >
                              <Close className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Matched lead or drop hint */}
                        {slot.matchedLead ? (
                          <div
                            className={`bg-white rounded-lg border shadow-sm px-3 py-2 ${
                              isSuggestedSlot
                                ? "border-amber-100"
                                : "border-emerald-100"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-1.5 mb-0.5">
                              <div className="flex items-center gap-1.5 min-w-0">
                                {isSuggestedSlot ? (
                                  <WatchLater className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                                ) : (
                                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                )}
                                <div className="text-sm text-slate-800 font-semibold truncate">
                                  {slot.matchedLead.displayName}
                                </div>
                              </div>
                              {mappingMode === "venmo" && (
                                <span
                                  className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                    isVenmoSuggestedSlot
                                      ? "bg-amber-100 text-amber-700"
                                      : "bg-emerald-100 text-emerald-700"
                                  }`}
                                >
                                  {isVenmoSuggestedSlot ? "Suggested" : "Confirmed"}
                                </span>
                              )}
                              {mappingMode === "appfolio" && (
                                <div className="flex-shrink-0 flex items-center gap-1">
                                  {(slot.matchedLead as MappedAppfolioMember)
                                    .source === "groupOccupant" && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-sky-100 text-sky-700">
                                      AppFolio Group
                                    </span>
                                  )}
                                  {(slot.matchedLead as MappedAppfolioMember)
                                    .source === "groupOccupant" && (
                                    <span
                                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                        isAppfolioSuggestedSlot
                                          ? "bg-amber-100 text-amber-700"
                                          : "bg-emerald-100 text-emerald-700"
                                      }`}
                                    >
                                      {isAppfolioSuggestedSlot
                                        ? "Suggested"
                                        : "Confirmed"}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {mappingMode === "venmo" ? (
                              <>
                                <div className="text-xs text-slate-500 pl-5 truncate mt-0.5">
                                  ${(slot.matchedLead as MappedVenmoCandidate).amount.toFixed(2)}
                                  {(slot.matchedLead as MappedVenmoCandidate)
                                    .paymentDate &&
                                    ` • ${new Date(
                                      (slot.matchedLead as MappedVenmoCandidate).paymentDate as string,
                                    ).toLocaleDateString()}`}
                                </div>
                                {isVenmoSuggestedSlot && (
                                  <div className="text-[11px] text-amber-600 pl-5 mt-0.5">
                                    Click Save to confirm this mapping
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {(slot.matchedLead as MappedAppfolioMember)
                                  .email && (
                                  <div
                                    className="text-[11px] text-slate-400 pl-5 truncate -mt-0.5"
                                    title={
                                      (slot.matchedLead as MappedAppfolioMember)
                                        .email
                                    }
                                  >
                                    {
                                      (slot.matchedLead as MappedAppfolioMember)
                                        .email
                                    }
                                  </div>
                                )}
                                {(slot.matchedLead as MappedAppfolioMember)
                                  .apartmentName && (
                                  <div className="text-xs text-slate-500 pl-5 truncate mt-0.5">
                                    {
                                      (slot.matchedLead as MappedAppfolioMember)
                                        .apartmentName
                                    }
                                  </div>
                                )}
                                {isAppfolioSuggestedSlot && (
                                  <div className="text-[11px] text-amber-600 pl-5 mt-0.5">
                                    Click Save to confirm this mapping
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-4 text-slate-400">
                            <DragIndicator className="w-4 h-4 mb-1 opacity-50" />
                            <span className="text-xs font-medium">
                              {isOver ? "Release to drop" : "Drop lead here"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right: All Public Leads (drag sources) */}
            <div className="w-1/2 flex flex-col overflow-hidden bg-slate-50/30">
              <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-100 flex items-center gap-2">
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex-1">
                  {mappingMode === "venmo"
                    ? "Venmo Payments"
                    : "Available Leads"}
                </p>
              </div>
              {/* Search */}
              <div className="px-3 py-3 border-b border-slate-100 bg-white">
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 transition-colors group-focus-within:text-blue-500" />
                  <input
                    type="text"
                    placeholder={
                      mappingMode === "venmo"
                        ? "Search by name..."
                        : "Search by name, email, or apartment..."
                    }
                    value={appfolioSearchQuery}
                    onChange={(e) => setAppfolioSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {mappingMode === "venmo" ? (
                  loading ? (
                    <div className="flex items-center justify-center h-20">
                      <Loading />
                    </div>
                  ) : availableVenmoCandidates.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center mt-6">
                      {appfolioSearchQuery
                        ? "No matching names found"
                        : "No Venmo payments found for this listing"}
                    </p>
                  ) : (
                    availableVenmoCandidates.map((candidate) => (
                      <div
                        key={candidate.dragId}
                        draggable
                        onDragStart={(e) =>
                          handleVenmoDragStart(e, candidate)
                        }
                        onDragEnd={handleVenmoDragEnd}
                        className="group flex items-center gap-2 p-2.5 rounded-xl border cursor-grab active:cursor-grabbing select-none transition-all duration-200 hover:shadow-sm border-slate-200 bg-white hover:border-slate-300"
                      >
                        <DragIndicator className="w-4 h-4 text-slate-300 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-700 font-semibold truncate">
                            {candidate.displayName}
                          </div>
                          <div className="text-xs text-slate-500 truncate mt-0.5">
                            ${candidate.amount.toFixed(2)}
                            {candidate.paymentDate &&
                              ` • ${new Date(candidate.paymentDate).toLocaleDateString()}`}
                          </div>
                        </div>
                      </div>
                    ))
                  )
                ) : loading ? (
                  <div className="flex items-center justify-center h-20">
                    <Loading />
                  </div>
                ) : filteredAppfolioData.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center mt-6">
                    {appfolioSearchQuery
                      ? "No matching leads found"
                      : "No public leads available"}
                  </p>
                ) : (
                  filteredAppfolioData.map((member) => {
                    const isAssigned = assignedLeadIds.has(member.dragId);
                    return (
                      <div
                        key={member.dragId}
                        draggable
                        onDragStart={(e) => handleDragStart(e, member)}
                        onDragEnd={handleDragEnd}
                        className={`group flex items-center gap-2 p-2.5 rounded-xl border cursor-grab active:cursor-grabbing select-none transition-all duration-200 hover:shadow-sm ${
                          isAssigned
                            ? "border-emerald-200 bg-emerald-50/50"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <DragIndicator className="w-4 h-4 text-slate-300 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="text-sm text-slate-700 font-semibold truncate">
                              {member.displayName}
                            </div>
                            {member.source === "groupOccupant" && (
                              <span className="flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-sky-100 text-sky-700">
                                AppFolio Group
                              </span>
                            )}
                          </div>
                          {member.email && (
                            <div
                              className="text-[11px] text-slate-400 truncate -mt-0.5"
                              title={member.email}
                            >
                              {member.email}
                            </div>
                          )}
                          {member.apartmentName && (
                            <div className="text-xs text-slate-500 truncate mt-0.5">
                              {member.apartmentName}
                            </div>
                          )}
                        </div>
                        {isAssigned && (
                          <span className="bg-emerald-100 text-emerald-600 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-bold">✓</span>
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save status toast */}
      {saveStatus && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div
            className={`px-4 py-2 rounded shadow-lg text-sm ${
              saveStatus === "success"
                ? "bg-green-100 text-green-800 border border-green-300"
                : "bg-red-100 text-red-800 border border-red-300"
            }`}
          >
            {saveStatus === "success"
              ? "Saved successfully!"
              : saveErrorMessage || "Error saving mappings"}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppfolioUnsignedList;
